import { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Search, X } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { MemberCard, SectionLoading } from '@/components/community/ui'
import { getMembers, setMemberRole, type MemberResponse, type CommunityRole } from '@/services/communityApi'

const ROLES: CommunityRole[] = ['MEMBER', 'VOLUNTEER', 'COORDINATOR', 'MODERATOR', 'LEADER']

export default function CommunityMembersPage() {
  const { t } = useTranslation()
  const { community, canManage } = useCommunity()
  const [members, setMembers] = useState<MemberResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('ALL')
  const [editing, setEditing] = useState<MemberResponse | null>(null)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    getMembers(community.id).then(setMembers).catch(() => setMembers([])).finally(() => setLoading(false))
  }, [community])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => members.filter((m) => {
    const q = query.trim().toLowerCase()
    const okQ = !q || m.user.fullName.toLowerCase().includes(q) || m.skills.join(' ').toLowerCase().includes(q) || m.interests.join(' ').toLowerCase().includes(q)
    const okR = roleFilter === 'ALL' || m.role === roleFilter
    return okQ && okR
  }), [members, query, roleFilter])

  if (!community) return null

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.members.title')} subtitle={t('community.members.subtitle')} icon={Users} />

      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('community.members.search')}
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none min-h-[44px]" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label={t('community.members.filterRole')}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none min-h-[44px]">
          <option value="ALL">{t('community.members.filterRole')}</option>
          {ROLES.map((r) => <option key={r} value={r}>{t(`community.role.${r}`, r)}</option>)}
        </select>
      </div>

      {loading ? <SectionLoading rows={4} /> : filtered.length === 0 ? (
        <GovEmptyState icon="👥" title={t('community.members.noMembers')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((m) => <MemberCard key={m.user.id} member={m} canManage={canManage} onSetRole={(mm) => setEditing(mm)} />)}
        </div>
      )}

      {editing && <RoleModal member={editing} onClose={() => setEditing(null)} onSaved={(r) => {
        setMembers((p) => p.map((x) => x.user.id === editing.user.id ? { ...x, role: r } : x))
        setEditing(null)
      }} />}
    </CommunityLayout>
  )
}

function RoleModal({ member, onClose, onSaved }: { member: MemberResponse; onClose: () => void; onSaved: (r: CommunityRole) => void }) {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [role, setRole] = useState<CommunityRole>(member.role)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!community) return
    setSaving(true); setError(null)
    try { await setMemberRole(community.id, member.user.id, role); onSaved(role) }
    catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.members.setRole')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.members.setRole')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">{member.user.fullName}</p>
        <select value={role} onChange={(e) => setRole(e.target.value as CommunityRole)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none min-h-[44px] mb-4">
          {ROLES.map((r) => <option key={r} value={r}>{t(`community.role.${r}`, r)}</option>)}
        </select>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
          <GovButton variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? t('common.saving') : t('community.actions.saveChanges')}</GovButton>
        </div>
      </div>
    </div>
  )
}